import { Grid } from '@mui/material';
import { AxisValueFormatterContext } from '@mui/x-charts/internals';
import { useQuery } from '@tanstack/react-query';
import produce from 'immer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { QueryToSqlHelper } from '../../../../../api/query-to-sql-helper';
import { DataValue, hashString } from '../../../../api/types';
import { toMetricResultOrError } from '../../../../api/utils';
import { TableViewMetrics } from '../../../../common/components/tableview/TableViewMetrics';
import { mergeSegmentations } from '../../../../common/utils';
import { UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import {
  useGlobalLocaleContext,
  useLatestPreferencesContext,
  useMetricDetailsMapContext,
  useMetricsQueryServiceContext,
} from '../../../../context/contexts';
import { DataFieldWithDataType, TimeSelection } from '../../../../types';
import { CHART_CONTENT_HEIGHT, CHART_TITLE_HEIGHT } from '../../../styles';
import { MetricTypes } from '../../../types';
import { TimePeriodBarChartComponentConfig } from '../../dashboards/types';
import { toSegmentation } from '../../filter/utils';
import { formatHierarchicalLabel } from '../../filter/utils-display';
import { ChartComponentProps } from '../chart-types';
import { ChartDataFetchError } from '../ChartDataFetchError';
import { ChartTitle } from '../ChartTitle';
import {
  useChartWidth,
  useGetMetricDisplayName,
  useSyncGlobalDisplayState,
  useToggleToolsOnSegmentationChange,
} from '../hooks';
import { BarChart } from '../mui-charts';
import { getChartTimePeriodAxisConfig } from '../mui-charts/label-utils';
import { toMuiChartsTimePeriodData } from '../mui-charts/utils-display';
import { useDisplayWithPersistence } from './hooks';
import { chartStyles } from './styles';
import { Tools } from './Tools';
import { timeSliderStateToTimePeriodTimeSelection } from './utils';

import { Hasher } from '../../../../api/hasher';
import { BetterLoading } from '../../BetterLoading';
import { ChartErrorDisplay } from '../../errors/components/ChartErrorDisplay';
import { LoadingCircle } from '../../LoadingCircle';
import { calcMarginBasedOnYAxisValues } from '../utils';
type ChartTimePeriodProps = ChartComponentProps<
  TimePeriodBarChartComponentConfig,
  | 'manualDataRefetchCountHandle'
  | 'timeSliderState'
  | 'filtersState'
  | 'segmentationState'
  | 'showTools'
  | 'fullScreenHandle'
  | 'displayGlobalState'
>;

export const ChartTimePeriod = (props: ChartTimePeriodProps) => {
  const {
    componentConfig,
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
    segmentationState,
    showTools,
    fullScreenHandle,
    displayGlobalState,
  } = props;
  const { t } = useTranslation();
  const metricService = useMetricsQueryServiceContext();

  const { title, queries, chartTypeConfig, colors } = componentConfig;
  const { metrics, timeSelection, filters, segmentations } = queries[0]; // TODO: make it work for multiple queries
  const finalTimeSelection: TimeSelection = timeSliderState
    ? timeSliderStateToTimePeriodTimeSelection(timeSliderState)
    : timeSelection;

  const allSegmentations = mergeSegmentations(
    segmentations,
    toSegmentation(segmentationState.items, MetricTypes.REGULAR),
    metrics,
    2
  );

  const displayHandle = useDisplayWithPersistence(
    componentConfig.chartId,
    chartTypeConfig,
    allSegmentations,
    metrics,
    displayGlobalState
  );
  const [displayState, displayReducer] = displayHandle;

  useSyncGlobalDisplayState(displayGlobalState, displayReducer);
  useToggleToolsOnSegmentationChange(
    segmentations,
    segmentationState,
    displayReducer
  );

  const allFilters =
    [
      QueryToSqlHelper.getFilterConditions(
        filtersState.items,
        MetricTypes.REGULAR,
        false
      ),
      filters,
    ]
      .flatMap((f) => (f ? [f] : []))
      .join(' AND ') || undefined;

  const [queryKey] = useDebounce(
    JSON.stringify([
      `chart-time-period`,
      hashString([
        ...metricService.getDeps(),
        Hasher.hash(metrics),
        Hasher.hash(finalTimeSelection),
        Hasher.hash(allFilters),
        Hasher.hash(allSegmentations),
      ]),
      manualDataRefetchCountHandle.current,
      displayState.selectedSortOrder,
    ]),
    500
  );

  const metricDetailsMap = useMetricDetailsMapContext();
  const domainPreferences = useLatestPreferencesContext();
  const locale = useGlobalLocaleContext();
  const formatLabel = (
    value: (DataValue | null)[],
    dataField: DataFieldWithDataType
  ) => formatHierarchicalLabel(value, dataField, t(UNDEFINED_DISPLAY_KEY), t);

  const { chartContainerRef, chartWidth } = useChartWidth();

  // I haven't supported employeeCohortMetrics in time period charts yet
  const { data, error, isPending } = useQuery({
    queryKey: [queryKey],
    queryFn: () => {
      return toMetricResultOrError(
        metricService,
        metrics,
        finalTimeSelection,
        allFilters,
        allSegmentations
      );
    },
  });

  const getMetricDisplayName = useGetMetricDisplayName();

  if (error) {
    return (
      <ChartErrorDisplay
        error={error}
        variant="inline"
        retry={() => manualDataRefetchCountHandle.increment()}
      />
    );
  }

  if (isPending) {
    return (
      <BetterLoading
        debugString="ChartTimePeriod"
        fallback={<LoadingCircle />}
        isPending={isPending}
      />
    );
  }

  const {
    series,
    tableViewData,
    barLabelConfig,
    metricSql,
    xAxisConfig,
    yAxisConfig,
  } = toMuiChartsTimePeriodData(
    data,
    allSegmentations,
    displayState,
    metricDetailsMap,
    formatLabel,
    getMetricDisplayName,
    colors,
    domainPreferences,
    t,
    chartWidth
  );

  // TODO: missing xAxisConfig and yAxisConfig would benefit from a dedicated error handling
  if (!xAxisConfig || !yAxisConfig) {
    return (
      <ChartDataFetchError
        retry={() => manualDataRefetchCountHandle.increment()}
      />
    );
  }

  const labels = (xAxisConfig.data ?? []).map(
    (d) =>
      xAxisConfig.valueFormatter?.(d, { location: 'tick' }) ??
      d.value?.[0] ??
      ''
  );
  const { tickRotation, tickFontSize, margins, maxVisibleTickLabelLength } =
    getChartTimePeriodAxisConfig(labels, chartWidth ?? 0);

  const valueFormatter = (d: any, context: AxisValueFormatterContext) => {
    let formattedTick = xAxisConfig.valueFormatter
      ? xAxisConfig.valueFormatter(d, { location: 'tick' })
      : d;
    if (context.location === 'tick') {
      formattedTick = formattedTick.slice(0, maxVisibleTickLabelLength);
    }
    return formattedTick;
  };

  const isRotated = tickRotation !== 0;
  const finalXAxisConfig = produce(xAxisConfig, (draft) => {
    draft.tickLabelStyle = {
      angle: tickRotation,
      textAnchor: isRotated ? 'end' : 'middle',
      fontSize: tickFontSize,
    };
    draft.valueFormatter = valueFormatter;
  });

  const leftYAxisValues =
    series[0]?.data?.map(
      (d) =>
        yAxisConfig[0].valueFormatter?.(d as number, { location: 'tick' }) ??
        String(d)
    ) ?? [];
  const leftYAxisTickValueBuffer =
    calcMarginBasedOnYAxisValues(leftYAxisValues);
  return (
    <Grid item container direction="column" sx={{ height: '100%' }}>
      <Grid item container sx={{ height: CHART_TITLE_HEIGHT }}>
        <ChartTitle
          metrics={metrics}
          metricsSql={metricSql}
          title={title?.[locale.selected]}
          timeSelection={finalTimeSelection}
          segmentations={allSegmentations}
          userFilters={allFilters}
        />
        {showTools ? (
          <Tools
            chartRef={chartContainerRef}
            fullScreenHandle={fullScreenHandle}
            displayHandle={displayHandle}
          />
        ) : null}
      </Grid>
      <Grid
        item
        container
        ref={chartContainerRef}
        sx={{ height: CHART_CONTENT_HEIGHT }}
      >
        {displayState.showTableView ? (
          <TableViewMetrics
            tableData={tableViewData}
            fullScreenHandle={fullScreenHandle}
            chartHeight={CHART_CONTENT_HEIGHT}
            showPercentage={displayState.showPercentage}
          />
        ) : (
          <BarChart
            sx={chartStyles()}
            title={title?.[locale.selected]}
            xAxis={[finalXAxisConfig]}
            yAxis={yAxisConfig}
            series={series}
            margin={{
              ...margins,
              left: margins.left + leftYAxisTickValueBuffer,
            }}
            slotProps={{
              popper: { container: () => fullScreenHandle.node.current },
              legend: {
                hidden: !displayState.showLegend,
                direction: 'column',
                position: {
                  vertical: 'top',
                  horizontal: 'right',
                },
                padding: {
                  top: 40,
                },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 5,
                itemGap: 5,
                labelStyle: {
                  fontSize: 12,
                },
              },
              noDataOverlay: { message: t('common:customDashboard.noData') },
            }}
            barLabel={barLabelConfig}
            grid={{
              vertical: displayGlobalState.showGridLines,
              horizontal: displayGlobalState.showGridLines,
            }}
            rightAxis={(yAxisConfig?.[1]?.id as string) ?? undefined}
            tooltip={{ trigger: 'axis' }}
          />
        )}
      </Grid>
    </Grid>
  );
};
